import React from 'react';
import { useTranslation } from 'react-i18next';
import stylesTable from './styles/ConsumptionMeteringPointGroup.module.scss';
import stylesAccordion from './styles/ConsumptionMeteringPointGroupAccordion.module.scss';

import Accordion from '../../Common/Accordion';
import Button from '../../Common/Button';
import Dialog from '../../Common/Dialog';
import Loading from '../../Common/Loading';
import ConsumptionMeteringPointGroupDelete from './ConsumptionMeteringPointGroupDelete';

// Actions.
import { fetchContractBasicData, fetchMeteringPointGroup } from '../../../actions';
// Types.
import { MeteringPointTableSorter, MeteringPointTableSorterConfig, SortableMeteringPoint } from '../../../types/billing';
import { MeteringPointGroup, MeteringPointGroupPut } from '../../../types/consumptionRates';
import { SimpleUser } from '../../../types/customer';
import { ContractBasicData } from '../../../types/contract';
// Utils.
import { generateAddress } from '../../../utils/address';

export type ConsumptionMeteringPointGroupAccordionProps = {
  type: 'create' | 'read' | 'update';
  user?: SimpleUser;
  group?: MeteringPointGroup;
  meteringPoints?: {
    meteringPoints: MeteringPointGroupPut[];
    setMeteringPoints: (value: React.SetStateAction<MeteringPointGroupPut[]>) => void;
  };
  setShowEditModal?: (value: React.SetStateAction<MeteringPointGroup | undefined>) => void;
  setRefreshData?: (value: React.SetStateAction<string>) => void;
};

// https://www.smashingmagazine.com/2020/03/sortable-tables-react/

const useSortableData = (items: SortableMeteringPoint[]): MeteringPointTableSorter => {
  const [sortConfig, setSortConfig] = React.useState<MeteringPointTableSorterConfig>();

  const sortedItems = React.useMemo((): SortableMeteringPoint[] => {
    const sortableItems = [...items];
    if (sortConfig !== undefined) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: keyof SortableMeteringPoint): void => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const ConsumptionMeteringPointGroupAccordion: React.FunctionComponent<ConsumptionMeteringPointGroupAccordionProps> = (
  props: ConsumptionMeteringPointGroupAccordionProps,
) => {
  const { type, user, group, setShowEditModal, meteringPoints, setRefreshData } = props;
  const { t } = useTranslation();

  const meteringPointsArr = meteringPoints?.meteringPoints ?? [];

  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = React.useState<MeteringPointGroup | undefined>(type === 'create' ? undefined : group);

  const [basicData, setBasicData] = React.useState<ContractBasicData[]>([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  const addresses: SortableMeteringPoint[] = [];
  if (type === 'create' && basicData) {
    basicData.forEach((mp) => {
      const item: SortableMeteringPoint = {
        streetName: mp.meteringpointAddress ? generateAddress(mp.meteringpointAddress, true) : '',
        postcode: mp.meteringpointAddress?.zipCode ?? '',
        cityName: mp.meteringpointAddress?.cityName ?? '',
        meteringPointIdentifier: mp.GSRN ?? '',
      };

      if (
        !addresses.find((a) => a.meteringPointIdentifier === item.meteringPointIdentifier) &&
        mp.meteringpointType &&
        mp.meteringpointType.toLowerCase() !== 'production'
      ) {
        addresses.push(item);
      }
    });
  }
  if (selectedGroup && selectedGroup.meteringPoints) {
    selectedGroup.meteringPoints.forEach((mp) => {
      const item: SortableMeteringPoint = {
        streetName: mp.meteringPoint?.address.addressViewFormShort ?? '',
        postcode: mp.meteringPoint?.address.postcode ?? '',
        cityName: mp.meteringPoint?.address.cityName ?? '',
        meteringPointIdentifier: mp.meteringPointIdentifier,
      };

      if (!addresses.find((a) => a.meteringPointIdentifier === item.meteringPointIdentifier)) {
        addresses.push(item);
      }
    });
  }

  const { items, requestSort, sortConfig } = useSortableData(addresses);

  const [loadBasicData, setLoadBasicData] = React.useState<boolean>(
    meteringPointsArr.filter((mp) => items.find((i) => i.meteringPointIdentifier === mp.meteringPointIdentifier)).length > 0 ? true : false,
  );

  React.useEffect(() => {
    let unMounted = false;

    async function fetchBasicData(): Promise<void> {
      if (user) {
        setLoading(true);
        const fetchedBasicData = await fetchContractBasicData(user.userId);

        if (fetchedBasicData !== null) {
          setBasicData(fetchedBasicData);

          setLoading(false);
        }
      }
    }
    async function fetchGroup(): Promise<void> {
      if (group) {
        setLoading(true);
        const fetchedGroup = await fetchMeteringPointGroup(parseInt(group.userIdentifier), group.identifier);
        if (!unMounted) {
          setSelectedGroup(fetchedGroup);
        }
        setLoading(false);
      }
    }

    if (type === 'create' && loadBasicData) {
      fetchBasicData();
    }
    if (type === 'update' && group && (group.meteringPoints.length === 0 || group.meteringPoints[0].meteringPoint === null)) {
      fetchGroup();
    }

    return (): void => {
      unMounted = true;
    };
  }, [user, group, loadBasicData]);

  const getClassNamesFor = (name: keyof SortableMeteringPoint): string | undefined => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const allSelected = meteringPoints?.meteringPoints.length === items.length;

  const accordionTitle = type === 'create' ? (user?.userName ?? '') : (selectedGroup?.groupName ?? '');

  return (
    <>
      {type === 'update' && (
        <Dialog openModal={showDeleteModal ? true : false} closeModal={() => setShowDeleteModal(showDeleteModal)}>
          {selectedGroup && showDeleteModal ? (
            <ConsumptionMeteringPointGroupDelete group={selectedGroup} setShowDeleteModal={setShowDeleteModal} setRefreshData={setRefreshData} />
          ) : (
            <></>
          )}
        </Dialog>
      )}
      <Accordion
        title={accordionTitle}
        className="contracts"
        icon={true}
        open={meteringPointsArr.filter((mp) => items.find((i) => i.meteringPointIdentifier === mp.meteringPointIdentifier)).length > 0}
        onClick={(): void => setLoadBasicData(true)}
      >
        {type !== 'create' ? (
          <header className={stylesTable.groupHeader}>
            <div className={stylesTable.buttons}>
              {type === 'update' && selectedGroup && (
                <Button type="button" className="buttonWhite" onClick={() => setShowDeleteModal(true)}>
                  {t(`common:general.deleteButton`)}
                </Button>
              )}
              <Button
                type="button"
                onClick={() => {
                  setShowEditModal && setShowEditModal(group);
                  setRefreshData && setRefreshData('groups');
                }}
              >
                {t(`common:general.editButton`)}
              </Button>
            </div>
          </header>
        ) : (
          <></>
        )}
        {items.length > 0 ? (
          <form className={`table-responsive ${stylesTable.tableWrapper} ${stylesAccordion.accordionTable}`}>
            <div className={stylesTable.search}>
              <input
                type="text"
                id="searchOptions"
                onChange={(e): void => setSearchQuery(e.currentTarget.value)}
                value={searchQuery}
                placeholder={t(`consumption:consumption.searchMeteringPoint`)}
              />
            </div>
            <table className={stylesTable.table}>
              <thead>
                <tr>
                  {type === 'create' && user && (
                    <th className={`form-item checkbox ${stylesTable.checkboxCell}`}>
                      <input
                        type="checkbox"
                        id={`selectAll-${user.userId}`}
                        className={stylesAccordion.checkbox}
                        onChange={(): void => {
                          const newMeteringPoints = [...meteringPointsArr].filter((i) => i.customerIdentifier !== user.userId.toString());
                          items.forEach((item) =>
                            newMeteringPoints.push({
                              meteringPointIdentifier: item.meteringPointIdentifier,
                              customerIdentifier: user.userId.toString() ?? '',
                            }),
                          );
                          if (allSelected) {
                            meteringPoints?.setMeteringPoints(newMeteringPoints.filter((i) => i.customerIdentifier !== user.userId.toString()));
                          } else {
                            meteringPoints?.setMeteringPoints(newMeteringPoints);
                          }
                        }}
                        checked={allSelected}
                      />
                      <label htmlFor={`selectAll-${user.userId}`} className="form-item-input"></label>
                    </th>
                  )}
                  <th onClick={(): void => requestSort('streetName')}>
                    {t(`common:general.streetAddress`)}
                    <span className={getClassNamesFor('streetName')} />
                  </th>
                  <th onClick={(): void => requestSort('postcode')}>
                    {t(`common:general.zipcode`)}
                    <span className={getClassNamesFor('postcode')} />
                  </th>
                  <th onClick={(): void => requestSort('cityName')}>
                    {t(`common:general.postalArea`)}
                    <span className={getClassNamesFor('cityName')} />
                  </th>
                  <th onClick={(): void => requestSort('meteringPointIdentifier')}>
                    {t(`contracts:customer.meteringpointId`)}
                    <span className={getClassNamesFor('meteringPointIdentifier')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter((o) => {
                    let searchMatches = true;
                    if (searchQuery !== '') {
                      searchMatches = JSON.stringify(o).toLowerCase().includes(searchQuery.toLowerCase());
                    }

                    return searchMatches;
                  })
                  .map((a) => {
                    const checked = meteringPointsArr.find((mp) => mp.meteringPointIdentifier === a.meteringPointIdentifier) ? true : false;

                    return (
                      <tr
                        key={`${user?.userId}-${a.meteringPointIdentifier}`}
                        className={checked ? stylesTable.selectedRow : undefined}
                        onClick={(): void => {
                          if (type === 'create') {
                            const newMeteringPoints = [...meteringPointsArr];
                            const newMeteringPoint = {
                              meteringPointIdentifier: a.meteringPointIdentifier,
                              customerIdentifier: user?.userId.toString() ?? '',
                            };
                            if (!checked) {
                              newMeteringPoints.push(newMeteringPoint);
                            } else {
                              const existingMeteringPoint = newMeteringPoints.find((mp) => mp.meteringPointIdentifier === a.meteringPointIdentifier);
                              if (existingMeteringPoint) {
                                newMeteringPoints.splice(newMeteringPoints.indexOf(existingMeteringPoint), 1);
                              }
                            }
                            meteringPoints?.setMeteringPoints(newMeteringPoints);
                          }
                        }}
                      >
                        {type === 'create' && (
                          <td className={`form-item checkbox ${stylesTable.checkboxCell}`}>
                            <input type="checkbox" className={stylesAccordion.checkbox} checked={checked} readOnly />
                            <label className="form-item-input"></label>
                          </td>
                        )}
                        <td>{a.streetName}</td>
                        <td>{a.postcode}</td>
                        <td>{a.cityName}</td>
                        <td>{a.meteringPointIdentifier}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </form>
        ) : (
          <>{loading && <Loading />}</>
        )}
      </Accordion>
    </>
  );
};

export default ConsumptionMeteringPointGroupAccordion;
